import { useEffect, useRef, useState } from "react";
import { pstyle } from "../lib/pstyle.js";
import Hoverable from "../lib/Hoverable.jsx";
import { useAuth } from "../lib/AuthContext.jsx";

const accent = "oklch(0.46 0.075 55)";
const ink = "oklch(0.26 0.015 60)";
const muted = "oklch(0.55 0.015 60)";
const line = "oklch(0.9 0.01 85)";
const danger = "oklch(0.5 0.09 30)";
const ok = "oklch(0.42 0.07 150)";

const field = pstyle(
  "width:100%; border:1px solid " + line + "; border-radius:7px; padding:10px 12px; font-size:13.5px; font-family:inherit; color:" + ink + "; background:oklch(1 0 0); outline:none; box-sizing:border-box;"
);

export default function Auth({ v }) {
  const { user, configured, signIn, signUp, resetPassword, signOut } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);
  // A fresh sign-in flips `user` via onAuthStateChange — leave the auth screen.
  useEffect(() => { if (user) v.goHome(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchMode = (m) => { setMode(m); setError(null); setNotice(null); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    let res;
    if (mode === "signin") res = await signIn(email, password);
    else if (mode === "signup") res = await signUp(email, password);
    else res = await resetPassword(email);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    if (mode === "signup") setNotice("Compte créé. Ouvrez l'e-mail de confirmation pour activer l'accès, puis connectez-vous.");
    else if (mode === "reset") setNotice("Si un compte existe pour cette adresse, un lien de réinitialisation vient d'être envoyé.");
    // signin success: the user effect above redirects home.
  };

  const title = mode === "signin" ? "Connexion" : mode === "signup" ? "Créer un compte" : "Mot de passe oublié";
  const sub =
    mode === "signin" ? "Retrouvez vos favoris, notes et progression sur tous vos appareils."
    : mode === "signup" ? "Un compte synchronise favoris, notes, avancement des cours et scores de quiz."
    : "Saisissez votre adresse : vous recevrez un lien pour définir un nouveau mot de passe.";
  const cta = busy ? "…" : mode === "signin" ? "Se connecter" : mode === "signup" ? "Créer le compte" : "Envoyer le lien";

  return (
    <div style={pstyle("min-height:calc(100vh - 60px); display:flex; align-items:flex-start; justify-content:center; padding:72px 24px 96px;")}>
      <div style={pstyle("width:100%; max-width:396px; display:flex; flex-direction:column; gap:26px;")}>
        <div style={pstyle("display:flex; flex-direction:column; gap:10px;")}>
          <span style={pstyle("display:flex; align-items:center; gap:8px; font-size:14px; font-weight:600; letter-spacing:-0.01em;")}>
            <span style={pstyle("width:9px; height:9px; border-radius:3px; background:" + accent + ";")}></span>netdoc
          </span>
          <h1 style={pstyle("margin:0; font-size:26px; font-weight:600; letter-spacing:-0.02em; color:" + ink + ";")}>{title}</h1>
          <p style={pstyle("margin:0; font-size:13.5px; line-height:1.6; color:" + muted + "; text-wrap:pretty;")}>{sub}</p>
        </div>

        {!configured && (
          <div style={pstyle("font-size:12.5px; line-height:1.55; color:" + danger + "; border:1px solid " + line + "; border-radius:8px; padding:12px 14px;")}>
            Backend non configuré (voir <span style={pstyle("font-family:'IBM Plex Mono', monospace;")}>SETUP.md</span>). En attendant, favoris, notes et progression sont enregistrés localement dans ce navigateur.
          </div>
        )}

        {user ? (
          <div style={pstyle("display:flex; flex-direction:column; gap:14px;")}>
            <div style={pstyle("font-size:13.5px; color:" + ink + ";")}>Déjà connecté : <span style={pstyle("font-family:'IBM Plex Mono', monospace;")}>{user.email}</span></div>
            <div style={pstyle("display:flex; gap:10px;")}>
              <Hoverable as="button" onClick={v.goHome}
                style={pstyle("border:none; border-radius:7px; padding:10px 16px; font-size:13.5px; font-family:inherit; cursor:pointer; background:" + ink + "; color:oklch(0.98 0.005 85);")}
                hoverStyle={{ background: "oklch(0.2 0.012 60)" }}>Aller au wiki</Hoverable>
              <Hoverable as="button" onClick={() => signOut()}
                style={pstyle("border:1px solid " + line + "; border-radius:7px; padding:10px 16px; font-size:13.5px; font-family:inherit; cursor:pointer; background:transparent; color:" + muted + ";")}
                hoverStyle={{ borderColor: "oklch(0.8 0.012 85)", color: ink }}>Se déconnecter</Hoverable>
            </div>
          </div>
        ) : (
          <>
            {mode !== "reset" && (
              <div style={pstyle("display:flex; border:1px solid " + line + "; border-radius:8px; padding:3px; gap:3px;")}>
                {[["signin", "Connexion"], ["signup", "Créer un compte"]].map(([m, label]) => (
                  <button key={m} type="button" onClick={() => switchMode(m)}
                    style={pstyle("border:none; flex:1; text-align:center; font-size:13px; padding:7px 0; border-radius:6px; cursor:pointer; font-family:inherit;", {
                      background: mode === m ? "oklch(0.97 0.006 85)" : "transparent",
                      color: mode === m ? ink : muted,
                      fontWeight: mode === m ? 600 : 400,
                    })}>{label}</button>
                ))}
              </div>
            )}

            <form onSubmit={submit} style={pstyle("display:flex; flex-direction:column; gap:12px;")}>
              <label style={pstyle("display:flex; flex-direction:column; gap:5px;")}>
                <span style={pstyle("font-size:12px; color:" + muted + ";")}>Adresse e-mail</span>
                <input ref={emailRef} type="email" required autoComplete="email" placeholder="vous@exemple.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} style={field} />
              </label>

              {mode !== "reset" && (
                <label style={pstyle("display:flex; flex-direction:column; gap:5px;")}>
                  <span style={pstyle("display:flex; align-items:baseline; font-size:12px; color:" + muted + ";")}>
                    Mot de passe
                    <button type="button" onClick={() => setShowPw((s) => !s)} style={pstyle("background:none; border:none; padding:0; font:inherit; margin-left:auto; cursor:pointer; color:" + accent + ";")}>{showPw ? "Masquer" : "Afficher"}</button>
                  </span>
                  <input type={showPw ? "text" : "password"} required minLength={6}
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    placeholder={mode === "signup" ? "6 caractères minimum" : "votre mot de passe"}
                    value={password} onChange={(e) => setPassword(e.target.value)} style={field} />
                </label>
              )}

              {error && <div style={pstyle("font-size:12.5px; line-height:1.5; color:" + danger + ";")}>{error}</div>}
              {notice && <div style={pstyle("font-size:12.5px; line-height:1.5; color:" + ok + ";")}>{notice}</div>}

              <Hoverable as="button" type="submit" disabled={busy}
                style={pstyle("margin-top:2px; border:none; border-radius:7px; padding:11px; font-size:14px; font-family:inherit; cursor:pointer; background:" + ink + "; color:oklch(0.98 0.005 85);", busy ? { opacity: 0.6, cursor: "default" } : null)}
                hoverStyle={busy ? {} : { background: "oklch(0.2 0.012 60)" }}>{cta}</Hoverable>
            </form>

            <div style={pstyle("display:flex; font-size:12.5px; color:" + muted + ";")}>
              {mode === "signin" && (
                <button type="button" onClick={() => switchMode("reset")} style={pstyle("background:none; border:none; padding:0; font:inherit; cursor:pointer; color:" + accent + ";")}>Mot de passe oublié ?</button>
              )}
              {mode === "reset" && (
                <button type="button" onClick={() => switchMode("signin")} style={pstyle("background:none; border:none; padding:0; font:inherit; cursor:pointer; color:" + accent + ";")}>← Revenir à la connexion</button>
              )}
              <button type="button" onClick={v.goHome} style={pstyle("background:none; border:none; padding:0; font:inherit; margin-left:auto; cursor:pointer; color:inherit;")}>Continuer sans compte</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
